/**
 * The price breakdown, as a short sheet over the booking form.
 *
 * On a phone the sidebar is not rendered at all for steps one to three —
 * `displayBottomNav` is the first term of `hideSidebar` in `App.tsx` — so the
 * sticky bar's subtotal is the only price those steps show, and until this
 * existed there was nowhere to ask what it was made of. This is that answer,
 * and it is deliberately the *same* ladder the cart prints, in the same order,
 * from the same `cartSummary` and `discountLinesFor`: a second arithmetic is
 * how the two surfaces come to disagree.
 *
 * Partial height on purpose. It sizes to its content and stops, so the bar it
 * opened from — and the Next button on it — stay visible and usable behind the
 * scrim. Clutter's equivalent disables its primary action while the sheet is
 * up; a customer who has just read the price is exactly the customer who wants
 * to continue, and making them close a sheet first is a step for nothing.
 *
 * It carries no close button for the same reason. The way out of a sheet that
 * covers nothing the customer wants is to carry on: press Next, tap the page
 * behind it, press Escape, or press the subtotal again — the caret on the bar
 * flips, so the control that opened it is visibly the control that shuts it.
 * A full-width Close would be the most prominent thing in a panel whose whole
 * job is to be read and dismissed without ceremony.
 */

import Divider from "antd/es/divider";
import React from "react";
import { useSelector } from "react-redux";
import { RootState } from "src";
import styled from "styled-components";
import { formatCents } from "@homero/pricing";
import { appointmentValueCentsOf, cartSummary, discountLinesFor } from "../../lib/price";
import { usePricingConfig } from "../../hooks/usePricingConfig";
import { AnimatedCents } from "./cart-motion";
import { FEE, SAVINGS } from "./savings";

export function SummarySheet({
  open,
  onClose,
  labelledBy,
  id,
}: {
  open: boolean;
  onClose: () => void;
  /** The bar trigger that owns this sheet, for the accessibility tree. */
  labelledBy: string;
  id: string;
}) {
  const booking = useSelector((state: RootState) => state.bookingReducer);
  const { occurance, code } = booking;

  /**
   * Subscribes this sheet to the rates arriving from the API.
   *
   * Not optional, and not covered by the bar that renders it: the config lives
   * in module state, so React only re-reads it in components that subscribe
   * (see `usePricingConfig`). Without this the sheet priced itself against the
   * bundled fallback card while the bar above it — which subscribes through
   * `usePrice` — showed the live one, and the two numbers on screen disagreed
   * by twenty dollars. The cart has always called this for the same reason.
   */
  usePricingConfig();

  const {
    breakdown,
    tipCents,
    creditsCents,
    creditsAppliedCents,
    amountDueCents,
    hasServiceDate,
    ongoingCents,
    totalLabel,
  } = cartSummary(booking);

  /**
   * Surge folded into the appointment value, exactly as the cart and
   * `toBookingLines` do it. Surge is what the appointment costs on that day,
   * not a fee bolted onto it; Same-Day is the opposite and keeps its own line,
   * because the customer chose it by name.
   */
  const appointmentValueCents = appointmentValueCentsOf(breakdown);

  const discountLines = discountLinesFor(breakdown, occurance, code);
  const discountCents = discountLines.reduce((sum, l) => sum + l.cents, 0);
  const discountPercent = appointmentValueCents
    ? Math.round((discountCents / appointmentValueCents) * 100)
    : 0;

  /** Escape closes it, like every other dismissible layer in the funnel. */
  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  /*
    Focus deliberately stays on the bar's trigger rather than moving in here.
    The trigger is what carries `aria-expanded`, so a screen reader announces
    the state change on the control the customer just used, and Escape or a
    second press closes it from where they already are. Nothing in the sheet is
    interactive, so there is nothing in it to move focus to.
  */

  return (
    <Sheet
        id={id}
        $open={open}
        role="dialog"
        aria-modal="false"
        aria-labelledby={labelledBy}
        /* Out of the screen reader's path when closed, as well as the eye's. */
        aria-hidden={open ? undefined : true}
      >
        <Grip aria-hidden="true" />
        <Rows>
          <Row>
            <span>Appointment Value:</span>
            <strong>
              <AnimatedCents cents={appointmentValueCents} />
            </strong>
          </Row>

          {breakdown.sameDaySurchargeCents > 0 ? (
            <Row>
              <span>Same-Day service:</span>
              <strong>
                <AnimatedCents
                  cents={breakdown.sameDaySurchargeCents}
                  prefix="+"
                />
              </strong>
            </Row>
          ) : null}

          {breakdown.travelPremiumCents > 0 ? (
            /* Amber, like every other place the funnel shows an avoidable charge. */
            <Row>
              <span>Travel fee:</span>
              <strong style={{ color: FEE.ink }}>
                <AnimatedCents
                  cents={breakdown.travelPremiumCents}
                  prefix="+"
                />
              </strong>
            </Row>
          ) : null}

          {discountCents > 0 ? (
            <>
              <Row>
                <span>
                  Discount{" "}
                  <Pill>
                    {"−"}
                    {discountPercent}%
                  </Pill>
                </span>
                <strong style={{ color: SAVINGS.ink }}>
                  <AnimatedCents cents={discountCents} prefix={"−"} />
                </strong>
              </Row>
              {/*
                Indented under the row they add up to, rather than in a separate
                block. The cart learned this the hard way: a customer who typed
                a code could not tell how much of the saving was the code and
                how much was the plan they are billed on every visit.
              */}
              {discountLines.map((line) => (
                <SubRow key={line.tag}>
                  <span>
                    <Tag>{line.tag}</Tag>
                    {line.label}
                  </span>
                  <em style={{ color: SAVINGS.ink }}>
                    <AnimatedCents cents={line.cents} prefix={"−"} />
                  </em>
                </SubRow>
              ))}
            </>
          ) : null}

          {breakdown.addOnsCents > 0 ? (
            <Row>
              <span>Add Ons:</span>
              <strong>
                <AnimatedCents cents={breakdown.addOnsCents} prefix="+" />
              </strong>
            </Row>
          ) : null}
        </Rows>

        <Divider style={{ color: "#B1DCE5", margin: "12px 0" }} />

        <Rows>
          <Row>
            <span>Subtotal:</span>
            <strong>
              <AnimatedCents cents={breakdown.subTotalCents} />
            </strong>
          </Row>
          <Row $quiet>
            <span>Tax:</span>
            <strong>
              <AnimatedCents cents={breakdown.taxCents} />
            </strong>
          </Row>
          <Row $quiet>
            <span>Tip:</span>
            <strong>
              <AnimatedCents cents={tipCents} />
            </strong>
          </Row>
          {creditsCents > 0 ? (
            <Row $quiet>
              <span>Credits:</span>
              <strong style={{ color: SAVINGS.ink }}>
                {formatCents(creditsAppliedCents)} (
                {formatCents(creditsCents - creditsAppliedCents)} remaining)
              </strong>
            </Row>
          ) : null}
        </Rows>

        <Divider style={{ color: "#B1DCE5", margin: "12px 0" }} />

        <TotalRow>
          <span>{totalLabel}</span>
          <strong>
            <AnimatedCents cents={amountDueCents} />
          </strong>
        </TotalRow>

        {/*
          What the subscription bills from the second cleaning on. The nearby
          discount is earned by one date's neighbourhood, so Stripe takes it off
          the first invoice alone and charges this every cycle after. Saying it
          here is what keeps the discount an incentive rather than a surprise.
        */}
        {ongoingCents !== null ? (
          <Note>
            Then {formatCents(ongoingCents)} per cleaning. Your nearby discount
            applies to the first one.
          </Note>
        ) : null}

        {hasServiceDate ? null : (
          /*
            Before a date is chosen the total is priced with every day-dependent
            axis collapsed, so it is a floor rather than a quote. Saying so is
            what makes the number honest.
          */
          <Note>Final price depends on the day and time you choose.</Note>
        )}
      </Sheet>
  );
}

/**
 * The dimmed page behind the sheet. Rendered by the bar as its own sibling,
 * outside the bar, so the bar stays undimmed above it while the sheet (which
 * lives inside the bar) sits on top of both.
 */
export function SummaryScrim({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  return <Scrim $open={open} onClick={onClose} aria-hidden="true" />;
}

const Scrim = styled.div<{ $open: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(16, 19, 34, 0.38);
  opacity: ${(p) => (p.$open ? 1 : 0)};
  pointer-events: ${(p) => (p.$open ? "auto" : "none")};
  transition: opacity 180ms ease;
  z-index: 2;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/*
  Docked on the bar's top edge rather than the viewport's bottom, so the bar —
  and the Next button on it — is never covered. The sheet is rendered inside the
  bar and hangs off its top with `bottom: 100%`, so it follows the bar's real
  height with nothing measured (a measured height went stale when the phone's
  safe-area inset changed, and the sheet floated 34px above the bar).
*/
const Sheet = styled.section<{ $open: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  /* The bar's padding box, so the sheet also covers its 1px top border. */
  bottom: 100%;
  background: #fff;
  border-radius: 16px 16px 0 0;
  padding: 8px 16px 16px;
  /* 100% is the bar's height here; dvh follows the iOS toolbar, vh is the fallback. */
  max-height: calc(100vh - 100% - 56px);
  max-height: calc(100dvh - 100% - 56px);
  overflow-y: auto;
  box-shadow: 0 -8px 28px rgba(20, 24, 36, 0.16);
  transform: translateY(${(p) => (p.$open ? "0" : "12px")});
  opacity: ${(p) => (p.$open ? 1 : 0)};
  /*
    Pointer-events alone is enough to make the closed sheet inert. Nothing
    inside it is focusable — every row is a p/span/strong — so there is no tab
    stop here to hide from the customer on their way to Next, and aria-hidden
    takes it out of the screen reader's path.
  */
  pointer-events: ${(p) => (p.$open ? "auto" : "none")};
  transition: transform 200ms cubic-bezier(0.32, 0.72, 0, 1),
    opacity 160ms ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    transform: none;
  }
`;

const Grip = styled.div`
  width: 36px;
  height: 4px;
  border-radius: 2px;
  background: #d7dae3;
  margin: 4px auto 12px;
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Row = styled.p<{ $quiet?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  margin: 0;
  font-size: 15px;
  color: ${(p) => (p.$quiet ? "#6b7280" : "#1f2430")};

  strong {
    font-weight: 600;
    color: inherit;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
`;

const SubRow = styled.p`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  margin: 0 0 0 12px;
  font-size: 13.5px;
  color: #6b7280;

  em {
    font-style: normal;
    font-weight: 500;
    white-space: nowrap;
    font-variant-numeric: tabular-nums;
  }
`;

const Tag = styled.span`
  display: inline-block;
  min-width: 42px;
  margin-right: 8px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: #9aa0ae;
`;

const Pill = styled.span`
  display: inline-block;
  padding: 1px 7px;
  border-radius: 999px;
  background: ${SAVINGS.tint};
  border: 1px solid ${SAVINGS.edge};
  color: ${SAVINGS.ink};
  font-size: 11.5px;
  font-weight: 600;
  vertical-align: 1px;
`;

const TotalRow = styled.p`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  margin: 0;
  font-size: 17px;
  font-weight: 600;
  color: #1f2430;

  strong {
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }
`;

const Note = styled.p`
  margin: 10px 0 0;
  font-size: 12.5px;
  line-height: 1.45;
  color: #6b7280;
`;

